/**
 * useCanvasStory — Generate a business model narrative from canvas data.
 *
 * Uses the story prompt template from interactive config and calls
 * the canvas story API endpoint (Phase 3). Falls back to a structured
 * summary if the API is unavailable.
 */

import { useState, useCallback, useMemo } from 'react'

import type { SectionItem } from '@ispot/shared'
import { canvasInteractiveApi } from '@/lib/api/client'

interface UseCanvasStoryOptions {
  storyPromptTemplate: string | undefined
  sectionItems: Record<string, SectionItem[]>
  canvasContext: Record<string, string[]>
  projectName?: string
  accountId?: string
  projectId?: string
  canvasInstanceUri: string | null
}

export function useCanvasStory({
  storyPromptTemplate,
  sectionItems,
  canvasContext,
  projectName,
  accountId,
  projectId,
  canvasInstanceUri,
}: UseCanvasStoryOptions) {
  const [story, setStory] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Build a structured fallback narrative from canvas data
  const fallbackStory = useMemo(() => {
    const parts: string[] = []
    const sections = canvasContext

    const vp = sections['value-propositions']
    const cs = sections['customer-segments']
    const ch = sections['channels']
    const cr = sections['customer-relationships']
    const ka = sections['key-activities']
    const kr = sections['key-resources']
    const kp = sections['key-partners']
    const cst = sections['cost-structure']
    const rs = sections['revenue-streams']

    if (vp?.length) {
      parts.push(
        `The business delivers value through ${vp.join(', ')}.`,
      )
    }

    if (cs?.length) {
      parts.push(`It serves ${cs.join(', ')} as primary customer segments.`)
    }

    if (ch?.length) {
      parts.push(`Customers are reached through ${ch.join(', ')}.`)
    }

    if (cr?.length) {
      parts.push(
        `Customer relationships are maintained via ${cr.join(', ')}.`,
      )
    }

    if (ka?.length || kr?.length) {
      const activities = ka?.length ? `key activities like ${ka.join(', ')}` : ''
      const resources = kr?.length ? `key resources including ${kr.join(', ')}` : ''
      const connector = activities && resources ? ' supported by ' : ''
      parts.push(`The model relies on ${activities}${connector}${resources}.`)
    }

    if (kp?.length) {
      parts.push(`Strategic partnerships with ${kp.join(', ')} strengthen the model.`)
    }

    if (rs?.length) {
      parts.push(`Revenue comes from ${rs.join(', ')}.`)
    }

    if (cst?.length) {
      parts.push(`Major costs include ${cst.join(', ')}.`)
    }

    if (parts.length === 0) {
      return 'Start filling in your canvas sections to generate a business model narrative. The story mode works best when you have at least 3-4 sections populated.'
    }

    return parts.join(' ')
  }, [canvasContext])

  const generateStory = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (accountId && projectId && canvasInstanceUri) {
        const result = await canvasInteractiveApi.generateStory(
          accountId,
          projectId,
          canvasInstanceUri,
          {
            canvasData: canvasContext,
            projectName: projectName || 'Business',
            templateId: 'business-model-canvas',
          },
        )
        setStory(result.narrative)
      } else {
        // Fallback when API context is unavailable
        setStory(fallbackStory)
      }
    } catch {
      setError('Failed to generate story')
      setStory(fallbackStory)
    } finally {
      setIsLoading(false)
    }
  }, [fallbackStory, accountId, projectId, canvasInstanceUri, canvasContext, projectName])

  const copyStory = useCallback(async () => {
    if (story) {
      await navigator.clipboard.writeText(story)
    }
  }, [story])

  return {
    story,
    isLoading,
    error,
    generateStory,
    copyStory,
    hasData: Object.keys(canvasContext).length > 0,
  }
}
